import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { describeAvailability, type AvailabilityTone } from '@/lib/menu/availabilityDisplay';
import { useTenantStore } from '@/stores/tenantStore';
import { useExportJob, type ExportDeliveryDestination } from '@/hooks/useExportJob';
import {
  adjustInventoryItem,
  fetchInventory,
  type InventoryItemRecord,
  type InventoryMutationResponse,
  updateInventoryItem,
} from '@/lib/api/inventory';
import { PackageSearch, AlertTriangle, Minus, Plus, RefreshCcw, Download } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { BackButton } from '@/components/BackButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface InventoryRowProps {
  item: InventoryItemRecord;
  onAdjust: (id: string, delta: number) => void;
  onReorderUpdate: (id: string, reorderPoint: number) => void;
  isAdjusting: boolean;
  isSavingReorder: boolean;
  now: number;
}

const AVAILABILITY_BADGE_CLASSES: Record<AvailabilityTone, string> = {
  positive: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  negative: 'bg-destructive/10 text-destructive',
  info: 'bg-sky-100 text-sky-700',
  muted: 'bg-muted text-muted-foreground',
};

const DEFAULT_AVAILABLE_MESSAGE = 'Available for ordering';
type ExportFormat = 'csv' | 'json';
const EMPTY_ITEMS: InventoryItemRecord[] = [];

const InventoryRow = ({ item, onAdjust, onReorderUpdate, isAdjusting, isSavingReorder, now }: InventoryRowProps) => {
  const [reorderValue, setReorderValue] = useState(() => item.reorderPoint.toString());
  const availability = item.menuItemAvailability;
  const availabilityDescriptor = availability ? describeAvailability(availability, now) : null;
  const availabilityBadgeClass = availabilityDescriptor ? AVAILABILITY_BADGE_CLASSES[availabilityDescriptor.tone] : AVAILABILITY_BADGE_CLASSES.muted;
  const availabilityPrimaryMessage = availabilityDescriptor && availabilityDescriptor.primaryMessage !== DEFAULT_AVAILABLE_MESSAGE ? availabilityDescriptor.primaryMessage : null;

  useEffect(() => {
    setReorderValue(item.reorderPoint.toString());
  }, [item.reorderPoint]);

  const availabilityStatus = availabilityDescriptor?.status ?? null;
  const countdownLabel = availabilityDescriptor?.countdownLabel ?? null;
  const lowStock = item.onHand <= item.reorderPoint;
  const soldOut = availabilityStatus === 'temporarily_unavailable';
  const scheduled = availabilityStatus === 'scheduled';
  const inactive = availabilityStatus === 'inactive' || availabilityStatus === 'expired';

  const handleReorderBlur = () => {
    const parsed = Number.parseInt(reorderValue, 10);

    if (Number.isNaN(parsed) || parsed < 0) {
      setReorderValue(item.reorderPoint.toString());
      return;
    }

    if (parsed === item.reorderPoint) {
      return;
    }

    onReorderUpdate(item.id, parsed);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      (event.target as HTMLInputElement).blur();
    }
    if (event.key === 'Escape') {
      setReorderValue(item.reorderPoint.toString());
      (event.target as HTMLInputElement).blur();
    }
  };

  return (
    <TableRow className={lowStock ? 'bg-amber-50/60' : undefined}>
      <TableCell>
        <div className="font-medium text-sm">{item.name}</div>
        <div className="text-xs text-muted-foreground flex flex-wrap gap-2 items-center">
          {item.unit && <span>Unit: {item.unit}</span>}
          {item.menuItemName && <span>Menu: {item.menuItemName}</span>}
        </div>
        {availabilityDescriptor ? (
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className={availabilityBadgeClass}>
              {availabilityDescriptor.statusLabel}
            </Badge>
            {availabilityPrimaryMessage ? (
              <span>
                {availabilityPrimaryMessage}
                {countdownLabel ? (
                  <span className="ml-1 text-muted-foreground/80">{`• ${countdownLabel}`}</span>
                ) : null}
              </span>
            ) : countdownLabel ? (
              <span>{countdownLabel}</span>
            ) : null}
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            disabled={isAdjusting || item.onHand <= 0}
            onClick={() => onAdjust(item.id, -1)}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <div className="min-w-[3rem] text-center font-semibold">{item.onHand}</div>
          <Button
            variant="outline"
            size="icon"
            disabled={isAdjusting}
            onClick={() => onAdjust(item.id, 1)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            value={reorderValue}
            onChange={(event) => setReorderValue(event.target.value)}
            onBlur={handleReorderBlur}
            onKeyDown={handleKeyDown}
            className="w-20 h-8 text-sm"
          />
          {isSavingReorder && <RefreshCcw className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      </TableCell>
      <TableCell>
        {inactive ? (
          <Badge variant="secondary">Hidden</Badge>
        ) : soldOut ? (
          <Badge variant="destructive">Sold out</Badge>
        ) : scheduled ? (
          <Badge variant="outline">Scheduled</Badge>
        ) : lowStock ? (
          <Badge variant="destructive">Reorder</Badge>
        ) : (
          <Badge variant="outline">Healthy</Badge>
        )}
      </TableCell>
    </TableRow>
  );
};

const InventoryPageV3 = () => {
  const { activeRestaurantId, slug: activeTenantSlug } = useTenantStore();
  const queryClient = useQueryClient();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const inventoryQuery = useQuery({
    queryKey: ['inventory', 'list', activeTenantSlug],
    queryFn: async ({ signal }) => {
      if (!activeTenantSlug) {
        throw new Error('Tenant not resolved');
      }
      const response = await fetchInventory(activeTenantSlug, signal);
      return response.items;
    },
    enabled: Boolean(activeRestaurantId && activeTenantSlug),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const items = useMemo(() => inventoryQuery.data ?? EMPTY_ITEMS, [inventoryQuery.data]);

  const adjustMutation = useMutation<InventoryMutationResponse, Error, { id: string; delta: number }>({
    mutationFn: ({ id, delta }) => {
      if (!activeTenantSlug) {
        return Promise.reject(new Error('Tenant not resolved'));
      }
      return adjustInventoryItem(activeTenantSlug, id, delta);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unexpected error';
      toast({ title: 'Unable to adjust inventory', description: message, variant: 'destructive' });
    },
  });

  const reorderMutation = useMutation<InventoryMutationResponse, Error, { id: string; reorderPoint: number }>({
    mutationFn: ({ id, reorderPoint }) => {
      if (!activeTenantSlug) {
        return Promise.reject(new Error('Tenant not resolved'));
      }
      return updateInventoryItem(activeTenantSlug, id, { reorderPoint });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unexpected error';
      toast({ title: 'Unable to update reorder point', description: message, variant: 'destructive' });
    },
  });

  const adjustingId = adjustMutation.variables?.id;
  const savingReorderId = reorderMutation.variables?.id;

  const lowStockItems = useMemo(() => items.filter((item) => item.onHand <= item.reorderPoint), [items]);

  const lastInventoryExportFormatRef = useRef<ExportFormat>('csv');

  const inventoryExport = useExportJob({
    target: 'inventory',
    getFilters: () => ({}),
    onSuccess: (job) => {
      if (job.delivery?.mode === 'webhook') {
        const destinationKey = (job.delivery.destination ?? 'inventory') as ExportDeliveryDestination;
        const destinationLabel = destinationKey === 'warehouse' ? 'warehouse' : 'inventory system';
        toast({
          title: 'Export dispatched',
          description: `Automation sent to ${destinationLabel}.`,
        });
        return;
      }
      const formatLabel = lastInventoryExportFormatRef.current.toUpperCase();
      toast({ title: 'Export ready', description: formatLabel + ' download started.' });
    },
    onError: (error) => {
      toast({
        title: 'Unable to export inventory',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleInventoryExport = useCallback(
    (format: ExportFormat) => {
      if (!items.length) {
        toast({
          title: 'Nothing to export',
          description: 'Adjust filters or sync inventory before exporting.',
          variant: 'destructive',
        });
        return;
      }

      lastInventoryExportFormatRef.current = format;
      inventoryExport.startExport(format);
    },
    [items.length, inventoryExport],
  );

  const handleInventoryAutomation = useCallback(
    (destination: ExportDeliveryDestination) => {
      if (!items.length) {
        toast({
          title: 'Nothing to export',
          description: 'Adjust filters or sync inventory before exporting.',
          variant: 'destructive',
        });
        return;
      }

      inventoryExport.startExport('csv', undefined, { mode: 'webhook', destination });
    },
    [inventoryExport, items.length],
  );


  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-5xl mx-auto space-y-8">
        <header className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <BackButton to="/dashboard" text="Back to Dashboard" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={items.length === 0 || inventoryExport.isRunning}
                >
                  <Download className={`mr-2 h-4 w-4 ${inventoryExport.isRunning ? 'animate-spin' : ''}`} />
                  {inventoryExport.isRunning ? 'Preparing…' : 'Export inventory'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={inventoryExport.isRunning}
                  onClick={() => handleInventoryExport('csv')}
                >
                  CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={inventoryExport.isRunning}
                  onClick={() => handleInventoryExport('json')}
                >
                  JSON
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Automations</DropdownMenuLabel>
                <DropdownMenuItem
                  disabled={inventoryExport.isRunning}
                  onClick={() => handleInventoryAutomation('inventory')}
                >
                  Send to inventory system
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={inventoryExport.isRunning}
                  onClick={() => handleInventoryAutomation('warehouse')}
                >
                  Send to warehouse
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <PackageSearch className="w-8 h-8 text-primary" />
            Inventory
          </h1>
          <p className="text-muted-foreground">Monitor stock levels, reorder points, and menu availability</p>
        </header>

        {lowStockItems.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/60">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Low Stock Alert
              </CardTitle>
              <CardDescription>
                {lowStockItems.length === 1
                  ? `${lowStockItems[0].name} is below its reorder point.`
                  : `${lowStockItems.length} items are below their reorder point.`}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">On Hand</CardTitle>
            <CardDescription>Adjust counts and reorder thresholds quickly</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="w-56">On Hand</TableHead>
                    <TableHead className="w-40">Reorder Point</TableHead>
                    <TableHead className="w-32">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventoryQuery.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                        Loading inventory...
                      </TableCell>
                    </TableRow>
                  ) : inventoryQuery.isError ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-destructive">
                        Failed to load inventory. Please refresh and try again.
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                        No inventory items yet. Add menu items to start tracking stock.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => (
                      <InventoryRow
                        key={item.id}
                        item={item}
                        onAdjust={(id, delta) => adjustMutation.mutate({ id, delta })}
                        onReorderUpdate={(id, reorderPoint) => reorderMutation.mutate({ id, reorderPoint })}
                        isAdjusting={Boolean(adjustMutation.isPending && adjustingId === item.id)}
                        isSavingReorder={Boolean(reorderMutation.isPending && savingReorderId === item.id)}
                        now={now}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InventoryPageV3;
